// src/polaris-components/BillingPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { Invoice, getInvoices } from '../utils/subscriptionApi';
import { PaymentMethodDisplay } from './PaymentMethodDisplay';
import { GroupedManagePlanPage } from './GroupedManagePlanPage';
import { ChangePaymentMethod } from './ChangePaymentMethod';
import { useAuth } from '../contexts/AuthContext';
import '../styles/billing-page.css';

type TabType = 'managePlan' | 'billing';

export const BillingPage: React.FC = () => {
  const navigate = useNavigate();
  const { userInfo } = useAuth();

  // Check if user is admin based on backend-validated role
  const isAdmin = userInfo?.role?.toLowerCase().includes('admin') || false;
  const isAthleteUser = userInfo?.role?.toLowerCase().includes('athlete') ||
                        userInfo?.role?.toLowerCase().includes('student') ||
                        false;

  // Redirect non-admin users
  useEffect(() => {
    if (!isAdmin) {
      if (isAthleteUser) {
        navigate('/athlete/profile', { replace: true });
      } else {
        navigate('/team', { replace: true });
      }
    }
  }, [navigate, isAdmin, isAthleteUser]);

  const {
    subscription,
    isLoading: subscriptionLoading,
    error: subscriptionError,
    refetch,
    isPastDue
  } = useSubscription();


  const [activeTab, setActiveTab] = useState<TabType>('managePlan');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showChangePaymentModal, setShowChangePaymentModal] = useState(false);

  const invoicesPerPage = 10;

  useEffect(() => {
    if (subscription) {
      fetchInvoices();
    }
  }, [subscription]);

  const fetchInvoices = async () => {
    setInvoicesLoading(true);
    setInvoicesError(null);
    try {
      const data = await getInvoices(50); // Fetch more for pagination
      setInvoices(data);
    } catch (err: any) {
      setInvoicesError('Unable to load invoices. Please try again later.');
    } finally {
      setInvoicesLoading(false);
    }
  };

  const handleManageBilling = () => {
    setShowChangePaymentModal(true);
  };

  const handlePaymentMethodUpdateSuccess = () => {
    refetch();
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';

    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const formatPrice = (amount: number, currency?: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
      minimumFractionDigits: 2
    }).format(amount);
  };

  const handleInvoiceClick = (invoice: Invoice) => {
    const invoiceUrl = invoice.invoicePdf || invoice.hostedInvoiceUrl;
    if (invoiceUrl) {
      window.open(invoiceUrl, '_blank', 'noopener,noreferrer');
    }
  };


  // Pagination logic
  const indexOfLastInvoice = currentPage * invoicesPerPage;
  const indexOfFirstInvoice = indexOfLastInvoice - invoicesPerPage;
  const currentInvoices = invoices.slice(indexOfFirstInvoice, indexOfLastInvoice);
  const totalPages = Math.ceil(invoices.length / invoicesPerPage);

  // Loading state
  if (subscriptionLoading) {
    return (
      <div className="billing-page" data-testid="billing-page">
        <div className="manage-plan-loading">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (subscriptionError) {
    return (
      <div className="billing-page" data-testid="billing-page">
        <div className="billing-error" data-testid="billing-error">
          <div className="error-icon">⚠️</div>
          <h2>Failed to Load Subscription</h2>
          <p>{subscriptionError}</p>
          <button onClick={refetch} className="retry-btn" data-testid="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No subscription state
  if (!subscription) {
    return (
      <div className="billing-page" data-testid="billing-page">
        <div className="no-subscription" data-testid="no-subscription">
          <div className="empty-icon">💳</div>
          <h2>No Active Subscription</h2>
          <p>You don't have an active subscription yet. Choose a plan to unlock all features!</p>
          <button
            onClick={() => navigate('/signup')}
            className="cta-btn"
            data-testid="choose-plan-btn"
          >
            Choose a Plan
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="billing-page" data-testid="billing-page">
      {/* Tabs */}
      <div className="billing-tabs" data-testid="billing-tabs">
        <button
          className={`tab-button ${activeTab === 'managePlan' ? 'active' : ''}`}
          onClick={() => setActiveTab('managePlan')}
          data-testid="manage-plan-tab"
        >
          Manage Plan
        </button>
        <button
          className={`tab-button ${activeTab === 'billing' ? 'active' : ''}`}
          onClick={() => setActiveTab('billing')}
          data-testid="billing-tab"
        >
          Billing
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'managePlan' ? (
        <GroupedManagePlanPage />
      ) : (
        <>
          {/* Page Header */}
          <div className="billing-header">
          </div>

          {/* Past Due Warning Banner */}
          {isPastDue && (
            <div className="billing-warning-banner" data-testid="billing-warning-banner">
              <div className="warning-content">
                <span className="warning-icon">⚠️</span>
                <div className="warning-text">
                  <strong>Payment Failed:</strong> Your payment method was declined.
                  Please update your payment method to avoid service interruption.
                </div>
              </div>
            </div>
          )}

          {/* Two Column Layout: Subscription + Payment Method */}
          <div className="billing-grid">
        {/* Current Plan Summary */}
        <div className="subscription-card" data-testid="subscription-card">
          <div className="card-header">
            <h2>Current Plan Summary</h2>
          </div>

          <div className="subscription-details">
            <div className="detail-group">
              <div className="detail-item-horizontal">
                <span className="detail-label">PLAN NAME</span>
                <span className="detail-value" data-testid="plan-name">
                  {subscription.planCategory === 'organization' && subscription.organizationDisplayName
                    ? `${subscription.organizationDisplayName} Plan`
                    : subscription.planName}
                </span>
              </div>
              <div className="detail-item-horizontal">
                <span className="detail-label">BILLING CYCLE</span>
                <span className="detail-value" data-testid="billing-interval">
                  {subscription.interval === 'day'
                    ? 'Daily'
                    : subscription.interval === 'month'
                      ? 'Monthly'
                      : 'Annual'}
                </span>
              </div>
              {subscription.planCategory !== 'organization' && (
                <div className="detail-item-horizontal">
                  <span className="detail-label">PLAN COST</span>
                  <span className="detail-value detail-amount" data-testid="subscription-amount">
                    {formatPrice(subscription.amount, subscription.currency)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment Method Card */}
        <div className="payment-method-section">
          <PaymentMethodDisplay
            paymentMethod={subscription.paymentMethod || null}
            onChangeClick={handleManageBilling}
          />
        </div>
      </div>

      {/* Billing History Section */}
      <div className="billing-history" data-testid="billing-history">
        <h2>Invoice</h2>

        {invoicesLoading ? (
          <div className="history-loading">
            <div className="spinner-small"></div>
            <span>Loading invoices...</span>
          </div>
        ) : invoicesError ? (
          <div className="history-error" data-testid="invoices-error">
            <p>{invoicesError}</p>
            <button onClick={fetchInvoices} className="retry-btn-small">
              Retry
            </button>
          </div>
        ) : invoices.length === 0 ? (
          <div className="empty-history" data-testid="empty-history">
            <p>No invoices yet</p>
          </div>
        ) : (
          <>
            <div className="invoices-table" data-testid="invoices-table">
              <div className="table-header">
                <span className="col-invoice-id">Invoice ID</span>
                <span className="col-billing-date">Billing Date</span>
                <span className="col-plan">Plan</span>
                <span className="col-amount">Amount</span>
                <span className="col-status">Status</span>
              </div>

              {currentInvoices.map((invoice) => (
                <div key={invoice.id} className="table-row" data-testid="invoice-row">
                  <span className="col-invoice-id">
                    {invoice.invoicePdf || invoice.hostedInvoiceUrl ? (
                      <button
                        type="button"
                        onClick={() => handleInvoiceClick(invoice)}
                        className="invoice-link"
                        data-testid="invoice-link"
                        title="Download invoice"
                      >
                        #{invoice.number || invoice.id.substring(0, 6)}
                      </button>
                    ) : (
                      <span>#{invoice.number || invoice.id.substring(0, 6)}</span>
                    )}
                  </span>
                  <span className="col-billing-date">{formatDate(invoice.created)}</span>
                  <span className="col-plan">{subscription.planName}</span>
                  <span className="col-amount">{formatPrice(invoice.amountPaid, invoice.currency)}</span>
                  <span className="col-status">
                    <span className={`invoice-status ${invoice.status}`}>
                      {invoice.status === 'paid' ? 'Paid' :
                       invoice.status === 'open' ? 'Pending' : invoice.status}
                    </span>
                  </span>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination" data-testid="pagination">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                  data-testid="prev-page-btn"
                >
                  Previous
                </button>
                <span className="pagination-info">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                  data-testid="next-page-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
        </div>
      </>
      )}

      {/* Change Payment Method Modal */}
      <ChangePaymentMethod
        isOpen={showChangePaymentModal}
        onClose={() => setShowChangePaymentModal(false)}
        onSuccess={handlePaymentMethodUpdateSuccess}
      />
    </div>
  );
};



